import React from 'react';
import {
  interpolateWheelRotationDeg,
  playerIdAtWheelAngle,
} from '@deadpan/shared';

/** Terracotta / cream clay palette — 6 seats max. */
const SECTOR_PALETTE = [
  { fill: '#D86A58', text: '#FFFFFF' },
  { fill: '#F4EDDD', text: '#393B3A' },
  { fill: '#B55245', text: '#FFFFFF' },
  { fill: '#E8D5BE', text: '#393B3A' },
  { fill: '#E08972', text: '#FFFFFF' },
  { fill: '#FBF3E8', text: '#393B3A' },
];

const ARROW_STROKE = '#6BA8D1';
const ARROW_FILL = '#D8F0FA';
const ARROW_SHAFT = '#A4D4EE';
const RIM = '#F8EBD8';
const RIM_EDGE = '#EAD6C0';

function polar(cx: number, cy: number, r: number, angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.sin(rad),
    y: cy - r * Math.cos(rad),
  };
}

function sectorPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const sweep = ((endDeg - startDeg + 360) % 360) > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${sweep} 1 ${end.x} ${end.y} Z`;
}

function truncateName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length <= 8) return trimmed;
  return `${trimmed.slice(0, 7)}…`;
}

interface GameTheoryWheelWheelProps {
  sectorPlayerIds: string[];
  playersById: Record<string, { name: string }>;
  selfId?: string;
  needleAngleDeg: number;
  highlightAskerId?: string;
  highlightAnswererId?: string;
}

export const GameTheoryWheelWheel: React.FC<GameTheoryWheelWheelProps> = ({
  sectorPlayerIds,
  playersById,
  selfId,
  needleAngleDeg,
}) => {
  const n = Math.max(1, sectorPlayerIds.length);
  const cx = 140;
  const cy = 140;
  const rimR = 124;
  const wheelR = 116;
  const sectorWidth = 360 / n;
  const fontSize = n > 4 ? 9 : n > 2 ? 11 : 13;

  // Tip is a triangle; tail is >> fletching. Outer extents stay equal around the hub.
  const strokeW = 5.2;
  const arrowHalf = 100;
  const pathHalf = arrowHalf - strokeW / 2;
  const headH = 30;
  const headW = 15.5;
  const chevronH = 16;
  const chevronGap = 10;
  const shaftW = 5.4;
  const shaftHalf = pathHalf - headH + 11;
  const tailShaft = pathHalf - chevronH - chevronGap + 6;
  const hubR = 17;

  return (
    <svg
      viewBox="0 0 280 280"
      className="w-full max-w-[340px] aspect-square"
      role="img"
    >
      <defs>
        <filter id="gtw-wheel-shadow" x="-18%" y="-18%" width="136%" height="136%">
          <feDropShadow dx="0" dy="10" stdDeviation="8" floodColor="#C4B6A8" floodOpacity="0.45" />
        </filter>
        <filter id="gtw-arrow-shadow" x="-40%" y="-20%" width="180%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="2.2" floodColor="#5A7A90" floodOpacity="0.28" />
        </filter>
        <filter id="gtw-hub-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" floodColor="#8A7464" floodOpacity="0.22" />
        </filter>
      </defs>

      <circle
        cx={cx}
        cy={cy}
        r={rimR}
        fill={RIM}
        stroke={RIM_EDGE}
        strokeWidth="1.25"
        filter="url(#gtw-wheel-shadow)"
      />

      {sectorPlayerIds.map((playerId, i) => {
        const start = i * sectorWidth - sectorWidth / 2;
        const end = i * sectorWidth + sectorWidth / 2;
        const color = SECTOR_PALETTE[i % SECTOR_PALETTE.length];
        return (
          <path
            key={`${playerId}-fill`}
            d={sectorPath(cx, cy, wheelR, start, end)}
            fill={color.fill}
          />
        );
      })}

      {sectorPlayerIds.map((playerId, i) => {
        const edge = i * sectorWidth - sectorWidth / 2;
        const inner = polar(cx, cy, 6, edge);
        const outer = polar(cx, cy, wheelR, edge);
        return (
          <line
            key={`${playerId}-div`}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke={RIM}
            strokeWidth="1.15"
          />
        );
      })}

      <g
        transform={`translate(${cx} ${cy}) rotate(${needleAngleDeg})`}
        filter="url(#gtw-arrow-shadow)"
      >
        <rect
          x={-shaftW / 2}
          y={-shaftHalf}
          width={shaftW}
          height={shaftHalf + tailShaft}
          rx={shaftW / 2}
          fill={ARROW_SHAFT}
        />
        <path
          d={`M 0 ${-pathHalf} L ${headW} ${-pathHalf + headH} L ${-headW} ${-pathHalf + headH} Z`}
          fill={ARROW_FILL}
          stroke={ARROW_STROKE}
          strokeWidth={strokeW}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={`M ${-headW} ${pathHalf} L 0 ${pathHalf - chevronH} L ${headW} ${pathHalf}`}
          fill="none"
          stroke={ARROW_STROKE}
          strokeWidth={strokeW}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={`M ${-headW} ${pathHalf - chevronGap} L 0 ${pathHalf - chevronGap - chevronH} L ${headW} ${pathHalf - chevronGap}`}
          fill="none"
          stroke={ARROW_STROKE}
          strokeWidth={strokeW}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={0} cy={0} r={hubR} fill="#FFFFFF" filter="url(#gtw-hub-shadow)" />
      </g>

      {sectorPlayerIds.map((playerId, i) => {
        const mid = i * sectorWidth;
        const color = SECTOR_PALETTE[i % SECTOR_PALETTE.length];
        const name = truncateName(playersById[playerId]?.name || '');
        const labelPos = polar(cx, cy, wheelR * 0.58, mid);
        const isSelf = playerId === selfId;
        return (
          <text
            key={`${playerId}-label`}
            x={labelPos.x}
            y={labelPos.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={color.text}
            stroke={color.text === '#FFFFFF' ? 'rgba(90,40,32,0.45)' : 'rgba(255,248,236,0.8)'}
            strokeWidth="2.4"
            strokeLinejoin="round"
            paintOrder="stroke"
            fontSize={fontSize}
            fontWeight={isSelf ? 800 : 700}
            fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif"
          >
            {name}
          </text>
        );
      })}
    </svg>
  );
};

export function useWheelNeedleAngle(
  stage: 'force' | 'spinning' | 'result' | undefined,
  totalRotationDeg: number | undefined,
  startedAt: number | undefined,
  endsAt: number | undefined,
  finalAngleDeg: number | undefined,
): number {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (stage !== 'spinning') return;
    let frame = 0;
    const tick = () => {
      setNow(Date.now());
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [stage, startedAt, endsAt]);

  if (stage === 'result') {
    return finalAngleDeg ?? 0;
  }
  if (stage === 'spinning' && totalRotationDeg != null && startedAt != null && endsAt != null) {
    return interpolateWheelRotationDeg(totalRotationDeg, startedAt, endsAt, now);
  }
  return 0;
}

export function liveHeadTail(
  sectorPlayerIds: string[],
  needleAngleDeg: number,
): { headId: string; tailId: string } {
  return {
    headId: playerIdAtWheelAngle(sectorPlayerIds, needleAngleDeg),
    tailId: playerIdAtWheelAngle(sectorPlayerIds, needleAngleDeg + 180),
  };
}
